import { BorderBox } from "@/components/atoms/BorderBox";
import { MarkSearchedPhrase } from "@/components/atoms/MarkSearchedPhrase";
import { TooltipButton } from "@/components/atoms/TooltipButton";
import { Button } from "@/components/ui/button";
import { CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { crmRegistry } from "@/features/crm_system/lib/_core/crmRegistry";
import { CrmSystemIds } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.types.gen";
import { useGetOrganizationCrmSystems } from "@/features/organization/hooks/utils/useGetOrganizationCrmSystems/useGetOrganizationCrmSystems";
import { CheckOrganizationAccess } from "@/features/rbac/components/molecules/CheckOrganizationAccess";
import { Tables } from "@/lib/supabase/supabase_types_override";
import { ArrowRight, CircleAlert, Loader } from "lucide-react";
import { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { HoverPopover } from "../../../../components/atoms/HoverPopover";
import { useIsEditOrganizationModalOpen } from "@/features/organization/hooks/utils/useIsEditOrganizationModalOpen";

interface ProjectRowProps<P extends Tables<'project'> = Tables<'project'>> {
    project: P;
    crmSystem?: CrmSystemIds | null;
    searchTerm: string;
    isLoading?: boolean;
    children: ReactNode;
    additionalActions?: (p: P) => ReactNode;
}

export function ProjectRow({
    project,
    crmSystem,
    searchTerm,
    isLoading = false,
    children,
    additionalActions
}: ProjectRowProps) {
    const orgCrmSystems = useGetOrganizationCrmSystems();
    const crmSystemConfig = crmRegistry.getSystemById(crmSystem);
    const { orgId } = useParams();
    const crmSystemNotPartOfOrganization = crmSystem && !orgCrmSystems?.map(system => system.id).includes(crmSystem);
    const { toggleEditOrganizationModal } = useIsEditOrganizationModalOpen();
    return (
        <BorderBox
            className="flex-col gap-2 border-l-4"
            style={{ borderLeftColor: crmSystemConfig?.branding.color }}
            size='sm'
            variant={crmSystemNotPartOfOrganization ? 'danger' : 'default'}
            icon={false}
        >

            <div className="flex items-center w-full gap-2">
                {crmSystemNotPartOfOrganization ? (
                    <HoverCard>
                        <HoverCardTrigger>

                            <div className="flex items-center gap-2">
                                <CircleAlert size={18} />
                                <div className="flex flex-col flex-grow">
                                    <div className="flex items-center gap-2">
                                        <MarkSearchedPhrase
                                            text={project.name ?? ''}
                                            searchTerm={searchTerm}
                                            className="text-base font-medium"
                                        />
                                        {isLoading && <Loader className="animate-spin" size={18} />}
                                    </div>
                                    {project.challenge && (
                                        <HoverPopover
                                            onHover
                                            content={project.challenge}
                                        >
                                            <MarkSearchedPhrase
                                                text={project.challenge}
                                                searchTerm={searchTerm}
                                                className="line-clamp-1 text-start text-muted-foreground"
                                            />
                                        </HoverPopover>
                                    )}
                                </div>
                            </div>
                        </HoverCardTrigger>
                        <HoverCardContent className="p-0 w-96">
                            <CardHeader>
                                <CardTitle>
                                    Invalid CRM System Connection
                                </CardTitle>
                                <CardDescription>
                                    The CRM system "{crmSystemConfig?.name}" is no longer connected to your organization.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                The CRM system associated with this project ({crmSystemConfig?.name}) is no longer connected to your organization. To use this project, please remove current CRM Project and select a different one or reconnect the CRM System to organization.
                            </CardContent>
                            <CardFooter className="justify-end">
                                <CheckOrganizationAccess
                                    organization_update
                                >
                                    <Button
                                        size='sm'
                                        onClick={toggleEditOrganizationModal}
                                    >
                                        Manage CRM Connections
                                    </Button>
                                </CheckOrganizationAccess>
                            </CardFooter>
                        </HoverCardContent>
                    </HoverCard>
                ) : (
                    <div className="flex flex-col flex-grow">
                        <div className="flex items-center gap-2">
                            <MarkSearchedPhrase
                                text={project.name ?? ''}
                                searchTerm={searchTerm}
                                className="text-base font-medium"
                            />
                            {isLoading && <Loader className="animate-spin" size={18} />}
                        </div>
                        {project.challenge && (
                            <HoverPopover
                                onHover
                                content={project.challenge}
                            >
                                <MarkSearchedPhrase
                                    text={project.challenge}
                                    searchTerm={searchTerm}
                                    className="line-clamp-1 text-start text-muted-foreground"
                                />
                            </HoverPopover>
                        )}
                    </div>
                )}
                <div className="flex flex-wrap gap-1 ml-auto lg:flex-nowrap">
                    {additionalActions && additionalActions(project)}

                    <TooltipButton
                        size='action'
                        variant='secondary'
                        tooltipContent="Open Project"
                        asChild
                    >
                        <Link to={`/organization/${orgId}/project/${project.id}/manage-project`}>
                            <ArrowRight size={16} />
                        </Link>
                    </TooltipButton>
                </div>
            </div>

            {children}

        </BorderBox>
    );
}